import { useEffect, useRef, useState } from "react";
import { Send, MessageCircle, X, User, Phone, Loader2 } from "lucide-react";
import { publicApi } from "@/lib/apiClient";
import { chatHistoryKey, currentChatUserId } from "@/lib/chatHistory";
import useChatHistory from "@/hooks/use-chat-history";

export const ChatWidget = ({ businessId, embedded = false, userId }) => {
  const [, refreshIdentity] = useState(0);
  useEffect(() => {
    const syncIdentity = (event) => {
      if (event.key === null || ["token", "currentUser"].includes(event.key)) refreshIdentity((version) => version + 1);
    };
    window.addEventListener("storage", syncIdentity);
    return () => window.removeEventListener("storage", syncIdentity);
  }, []);
  const ownerId = userId === undefined ? currentChatUserId() : userId;
  const storageKey = chatHistoryKey(ownerId, businessId);
  return <ChatConversation key={storageKey} businessId={businessId} embedded={embedded} storageKey={storageKey} />;
};

const ChatConversation = ({ businessId, embedded, storageKey }) => {
  const [config, setConfig] = useState(null);
  const [open, setOpen] = useState(embedded);
  const { messages, appendMessage, ensureGreeting, getSessionId, mounted } = useChatHistory(storageKey);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [showLead, setShowLead] = useState(false);
  const [leadName, setLeadName] = useState("");
  const [leadWa, setLeadWa] = useState("");
  const [leadSent, setLeadSent] = useState(false);
  const [logoErr, setLogoErr] = useState(false);
  const scrollRef = useRef(null);
  const leadTimer = useRef(null);

  useEffect(() => () => clearTimeout(leadTimer.current), []);

  useEffect(() => {
    let cancelled = false;
    publicApi
      .get(`/public/business/${businessId}`)
      .then((r) => {
        if (cancelled) return;
        setConfig(r.data);
        ensureGreeting(r.data.greeting);
      })
      .catch(() => {
        if (!cancelled) setConfig({ name: "Chat", brand_color: "#2563EB", widget_position: "bottom-right", greeting: "Halo!" });
      });
    return () => { cancelled = true; };
  }, [businessId, ensureGreeting]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, loading, showLead]);

  const send = async (override) => {
    const text = (override ?? input).trim();
    if (!text || loading) return;
    if (override === undefined) setInput("");
    appendMessage({ role: "user", content: text });
    setLoading(true);
    try {
      const r = await publicApi.post(`/public/chat/${businessId}`, { session_id: getSessionId(), message: text });
      appendMessage({ role: "assistant", content: r.data.reply });
    } catch {
      appendMessage({ role: "assistant", content: "Maaf, terjadi kendala. Silakan tinggalkan kontak Anda." });
    } finally {
      if (mounted.current) setLoading(false);
    }
  };

  const submitLead = async () => {
    if (!leadName.trim() || !leadWa.trim()) return;
    const lastUser = [...messages].reverse().find((m) => m.role === "user");
    try {
      const r = await publicApi.post(`/public/lead/${businessId}`, {
        name: leadName,
        whatsapp: leadWa,
        question: lastUser?.content || "",
      });
      appendMessage({ role: "assistant", content: r.data.message });
      if (!mounted.current) return;
      setLeadSent(true);
      leadTimer.current = setTimeout(() => {
        setShowLead(false);
        setLeadSent(false);
        setLeadName("");
        setLeadWa("");
      }, 1800);
    } catch {
      appendMessage({ role: "assistant", content: "Gagal mengirim kontak. Coba lagi." });
    }
  };

  const brand = config?.brand_color || "#2563EB";
  const posClass = config?.widget_position === "bottom-left" ? "left-4" : "right-4";

  const Panel = (
    <div
      className={`flex flex-col bg-[#FFFFFF] border border-[#E2E8F0] overflow-hidden ${
        embedded ? "w-full h-full rounded-none" : "w-[370px] max-w-[calc(100vw-32px)] h-[540px] max-h-[calc(100dvh-110px)] rounded-2xl shadow-2xl"
      }`}
      data-testid="chat-widget-panel"
    >
      <div className="px-4 py-3 flex items-center justify-between" style={{ backgroundColor: brand }}>
        <div className="flex items-center gap-2.5 text-white">
          {config?.logo_url && !logoErr ? (
            <img src={config.logo_url} alt={config?.name || "logo"} data-testid="chat-widget-logo" className="w-9 h-9 rounded-full object-cover border-2 border-white/40 shrink-0" onError={() => setLogoErr(true)} />
          ) : (
            <div className="w-9 h-9 rounded-full bg-white/15 flex items-center justify-center shrink-0" data-testid="chat-widget-logo-fallback">
              <MessageCircle size={18} />
            </div>
          )}
          <div>
            <p className="font-heading font-bold text-sm leading-tight">{config?.name || "Asisten"}</p>
            <p className="text-[11px] opacity-80 leading-tight">Biasanya membalas instan</p>
          </div>
        </div>
        {!embedded && (
          <button onClick={() => setOpen(false)} className="text-white/90 hover:text-white" data-testid="chat-widget-close">
            <X size={18} />
          </button>
        )}
      </div>

      <div ref={scrollRef} className="flex-1 overflow-y-auto p-3 space-y-3 bg-[#F8FAFC]">
        {messages.map((m, i) => (
          <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
            <div
              className={`max-w-[80%] px-3 py-2 rounded-2xl text-sm whitespace-pre-wrap break-words [overflow-wrap:anywhere] leading-relaxed ${
                m.role === "user" ? "text-white rounded-br-sm" : "bg-[#F1F5F9] text-slate-900 rounded-bl-sm"
              }`}
              style={m.role === "user" ? { backgroundColor: brand } : {}}
            >
              {m.content}
            </div>
          </div>
        ))}
        {(config?.quick_replies || []).length > 0 && !messages.some((m) => m.role === "user") && (
          <div className="flex flex-wrap gap-2 pt-1" data-testid="quick-replies">
            {config.quick_replies.map((q, i) => (
              <button
                key={i}
                onClick={() => send(q)}
                disabled={loading}
                data-testid={`quick-reply-${i}`}
                className="text-xs bg-[#F1F5F9] border border-[#E2E8F0] rounded-full px-3 py-1.5 text-slate-800 hover:border-[#3B82F6] hover:text-slate-900 transition-colors disabled:opacity-50"
              >
                {q}
              </button>
            ))}
          </div>
        )}
        {loading && (
          <div className="flex justify-start">
            <div className="bg-[#F1F5F9] text-slate-700 px-3 py-2 rounded-2xl rounded-bl-sm text-sm flex items-center gap-2">
              <Loader2 size={14} className="animate-spin" /> mengetik...
            </div>
          </div>
        )}

        {showLead && (
          <div className="bg-[#F1F5F9] border border-[#E2E8F0] rounded-xl p-3 space-y-2 animate-fade-up">
            <p className="text-xs text-slate-700 font-medium">Tinggalkan kontak, tim kami akan menghubungi Anda:</p>
            <div className="flex items-center gap-2 bg-[#FFFFFF] border border-[#E2E8F0] rounded-lg px-2">
              <User size={14} className="text-slate-500" />
              <input
                value={leadName}
                onChange={(e) => setLeadName(e.target.value)}
                placeholder="Nama"
                data-testid="chat-widget-lead-name"
                className="bg-transparent min-w-0 flex-1 py-2 text-sm text-slate-900 outline-none"
              />
            </div>
            <div className="flex items-center gap-2 bg-[#FFFFFF] border border-[#E2E8F0] rounded-lg px-2">
              <Phone size={14} className="text-slate-500" />
              <input
                value={leadWa}
                onChange={(e) => setLeadWa(e.target.value)}
                placeholder="Nomor WhatsApp (contoh 08xxx)"
                data-testid="chat-widget-lead-wa"
                className="bg-transparent min-w-0 flex-1 py-2 text-sm text-slate-900 outline-none"
              />
            </div>
            <button
              onClick={submitLead}
              disabled={leadSent}
              data-testid="chat-widget-lead-submit-button"
              className="w-full py-2 rounded-lg text-white text-sm font-semibold transition-colors disabled:opacity-60"
              style={{ backgroundColor: brand }}
            >
              {leadSent ? "Terkirim ✓" : "Kirim Kontak"}
            </button>
          </div>
        )}
      </div>

      <div className="p-2 border-t border-[#E2E8F0] bg-[#FFFFFF]">
        {!showLead && (
          <button
            onClick={() => setShowLead(true)}
            data-testid="chat-widget-leave-contact"
            className="w-full mb-2 text-xs text-[#25D366] font-medium hover:underline"
          >
            Tinggalkan nama & nomor WhatsApp
          </button>
        )}
        <div className="flex items-center gap-2">
          <input
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && send()}
            placeholder="Tulis pesan..."
            data-testid="chat-widget-input"
            className="min-w-0 flex-1 bg-[#F1F5F9] border border-[#E2E8F0] rounded-full px-4 py-2 text-sm text-slate-900 outline-none focus:border-[#3B82F6] transition-colors"
          />
          <button
            onClick={() => send()}
            data-testid="chat-widget-send-button"
            className="w-9 h-9 rounded-full flex items-center justify-center text-white shrink-0 transition-transform hover:scale-105 active:scale-95"
            style={{ backgroundColor: brand }}
          >
            <Send size={16} />
          </button>
        </div>
      </div>
    </div>
  );

  if (embedded) return Panel;

  return (
    <div className={`fixed bottom-4 ${posClass} z-50 flex flex-col items-end gap-3`}>
      {open && <div className="animate-fade-up">{Panel}</div>}
      <button
        onClick={() => setOpen((o) => !o)}
        data-testid="chat-widget-trigger-button"
        className={`w-14 h-14 rounded-full shadow-2xl flex items-center justify-center text-white transition-transform hover:scale-105 active:scale-95 ${config?.widget_position === "bottom-left" ? "self-start" : "self-end"}`}
        style={{ backgroundColor: brand }}
      >
        {open ? <X size={24} /> : <MessageCircle size={24} />}
      </button>
    </div>
  );
};
